/**
 * 계측 로그 저장과 내보내기 (SPEC S-10).
 *
 * **원격 전송이 없다.** 이 모듈에는 `fetch`, `XMLHttpRequest`, `sendBeacon`,
 * WebSocket이 없고 앞으로도 없어야 한다. 로그는 브라우저 저장소에만 쌓이고
 * 사용자가 JSON으로 내려받는 경로만 제공한다(AC-25).
 *
 * 저장소를 주입받는 이유는 두 가지다. 테스트를 DOM 없이 돌릴 수 있고,
 * `localStorage`가 막힌 환경(프라이빗 모드, 사이트 데이터 차단)에서 메모리
 * 저장소로 조용히 물러날 수 있다.
 */

import { textHash } from './canonical';
import {
  createConfirmationEmitter,
  type ConfirmationEmitterStats,
  type ConfirmationSink,
  type FailureReason,
  type StorageKeyName,
} from './confirmation';
import {
  createEvent,
  validateEvent,
  type EventClock,
  type TelemetryEvent,
  type TelemetryEventName,
  type TelemetryPayload,
} from './events';

export interface TelemetryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const STORAGE_KEY = 'ccatlas.telemetry';

/**
 * 상한에 닿아 버린 이벤트 수를 적어 두는 자리.
 *
 * 이벤트 배열과 **다른 키에 둔다.** 같은 배열에 넣으면 그 표시 자체가 상한에
 * 밀려 사라지고, 잃어버린 사실을 잃어버리게 된다.
 */
export const DROPPED_KEY = 'ccatlas.telemetry.dropped';

/**
 * 되살리지 못한 항목 수를 적어 두는 자리 (REV-017).
 *
 * 손상된 항목은 다음 저장 때 정리되어 저장소에서 사라진다. 그때 이 셈까지
 * 함께 사라지면 "무언가 잃었다"는 사실만 조용히 없어진다.
 */
export const REJECTED_KEY = 'ccatlas.telemetry.rejected';

/**
 * 만들어지지도 못하고 버려진 이벤트 수를 적어 두는 자리 (REV-023).
 *
 * 스키마를 어긴 이벤트는 저장소에 닿기 전에 거부된다. 그 일이 있었다는
 * 사실까지 사라지면, 계측이 조용히 빠진 로그가 완전한 기록으로 보인다.
 */
export const INVALID_KEY = 'ccatlas.telemetry.invalid';

/**
 * 보관하는 최대 이벤트 수.
 *
 * 중앙 온라인 명세 F1-05가 고정한 세션 이벤트 상한과 같은 값이다. 한때 500
 * 이었는데, 그것은 저장소 용량만 보고 정한 수였고 승인된 계약보다 낮았다.
 * 그 사이 구간의 이벤트는 계약상 남아 있어야 하는데 조용히 사라졌다(REV-013).
 */
export const MAX_EVENTS = 2000;

export function createMemoryStorage(): TelemetryStorage {
  const map = new Map<string, string>();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    },
  };
}

export interface ResolvedStorage {
  readonly storage: TelemetryStorage;
  /**
   * 브라우저 저장소를 실제로 쓸 수 있는가.
   *
   * `false`면 메모리로 물러난 것이고, 이 세션의 기록은 창을 닫는 순간
   * 사라진다. **이 사실을 로그까지 가져가야 한다** —— 물러난 저장소는
   * 쓰기가 잘 되므로 로그 입장에서는 아무 문제가 없어 보이고, 그래서
   * 예전에는 `complete: true`를 내놓았다(REV-014).
   */
  readonly persistent: boolean;
}

/**
 * 브라우저 저장소. 접근 자체가 던질 수 있어(사이트 데이터 차단, 프라이빗
 * 모드) 한 번 시험 삼아 써 보고, 실패하면 메모리 저장소로 물러난다.
 *
 * 물러난 사실을 함께 돌려준다. 조용히 물러나면 "저장했다"와 "저장한 척했다"가
 * 구별되지 않는다.
 */
export function resolveBrowserStorage(): ResolvedStorage {
  try {
    const probe = '__ccatlas_probe__';
    window.localStorage.setItem(probe, '1');
    /*
     * **읽기까지 확인한다** (REV-020).
     *
     * 쓰기만 시험하면 `getItem`이 던지는 저장소를 정상으로 보게 된다. 그런
     * 저장소에서는 읽을 때마다 빈 로그가 돌아오고, 그 위에 새 기록을 쓰면
     * 앞선 기록이 지워진다. 써 넣은 값이 그대로 돌아오는 것까지 확인해야
     * 저장소로 쓸 수 있다고 말할 수 있다.
     */
    const echo = window.localStorage.getItem(probe);
    window.localStorage.removeItem(probe);
    if (echo !== '1') {
      return { storage: createMemoryStorage(), persistent: false };
    }
    return { storage: window.localStorage, persistent: true };
  } catch {
    return { storage: createMemoryStorage(), persistent: false };
  }
}

/**
 * 이 로그의 완전성 보고 (REV-024).
 *
 * 화면과 내보내기 파일이 **같은 값**을 보게 하려고 한자리에 모은다. 사유를
 * 따로따로 물어 오면 한쪽만 일부를 골라 보여 주는 일이 생긴다 —— 실제로
 * 화면이 상한 손실만 보이고 순번 충돌을 숨긴 적이 있다.
 */
export interface TelemetryIntegrity {
  readonly complete: boolean;
  readonly storage: StorageStatus;
  /** 상한에 밀려 버린 이벤트 수. */
  readonly dropped: number;
  /** 저장된 값에서 되살리지 못한 항목 수. */
  readonly rejected: number;
  /** 만들어지지 못하고 거부된 이벤트 수. */
  readonly invalid: number;
  /** 남은 기록 가운데 비어 있는 순번 수. */
  readonly sequenceGaps: number;
  /** 순번이 겹치거나 뒤집힌 자리 수. */
  readonly sequenceConflicts: number;
  /** 순번이 아예 없는 기록 수. */
  readonly unsequenced: number;
  /** 첫 순번 앞의 설명되지 않는 자리 수. */
  readonly unexplainedLeading: number;
  /** 손실 셈을 적어 둔 보조 값이 손상되었는가. */
  readonly metaCorrupt: boolean;
  /**
   * 손실 셈을 **읽지 못했는가** (REV-025).
   *
   * 손상과 다른 사유다. 값이 망가진 것이 아니라 무엇인지 알 수 없는 것이며,
   * 그래서 0으로 갈음할 수도 덮어쓸 수도 없다.
   */
  readonly metaUnreadable: boolean;
}

export interface TelemetryLog {
  record(name: TelemetryEventName, payload?: TelemetryPayload): TelemetryEvent | undefined;
  readAll(): readonly TelemetryEvent[];
  /** 기록을 비운다. **실제로 비웠으면** `true`. */
  clear(): boolean;
  /** 내보내기용 JSON. 사용자가 파일로 내려받는 내용이다. */
  toJson(): string;
  size(): number;
  /** 상한에 닿아 버린 이벤트 수. 0이면 아무것도 잃지 않았다. */
  dropped(): number;
  /**
   * 저장된 값에서 되살리지 못한 항목 수 (REV-017).
   *
   * 손상되어 스키마를 통과하지 못한 기록이다. 로그 전체가 깨져 몇 건이었는지
   * 알 수 없으면 최소 1로 센다.
   */
  rejected(): number;
  /** 남은 기록 가운데 비어 있는 순번의 수. 설명되지 않는 누락의 흔적이다. */
  sequenceGaps(): number;
  /**
   * 순번이 앞으로 나아가지 않은 자리의 수 (REV-018).
   *
   * 중복이거나 뒤로 돌아간 순번이다. 저장 순서를 더는 보증하지 못한다.
   */
  sequenceConflicts(): number;
  /** 순번이 아예 없는 기록의 수 (REV-019). */
  unsequenced(): number;
  /**
   * 만들어지지 못하고 거부된 이벤트 수 (REV-023).
   *
   * 스키마를 어겨 저장소에 닿기 전에 버려진 것들이다. 호출자가 `record()`의
   * 반환값을 보지 않아도 이 셈에 남으므로, 빠진 계측이 조용히 묻히지 않는다.
   */
  invalid(): number;
  /**
   * 첫 순번 앞에 설명되지 않는 자리가 몇 개인가 (REV-021).
   *
   * 남은 기록이 1번이 아니라 6번부터 시작하는데 버린 셈이 0이면, 앞의 다섯은
   * 어디로 갔는지 설명되지 않는다. 상한이나 손상으로 설명되면 0이다.
   */
  unexplainedLeading(): number;
  /** 손실 셈을 적어 둔 보조 값이 손상되었는가 (REV-021). */
  metaCorrupt(): boolean;
  /** 손실 셈을 읽지 못했는가 (REV-025). 손상과 다른 사유다. */
  metaUnreadable(): boolean;
  /**
   * 완전성 사유를 한 번에 모아 돌려준다 (REV-024).
   *
   * 화면은 이것 하나만 보면 된다. 사유를 따로 묻지 않으므로 일부만 골라
   * 보여 주는 일이 생기지 않는다.
   */
  integrity(): TelemetryIntegrity;
  /**
   * 이 로그가 세션 전체를 담고 있고 제대로 보관되었는가.
   *
   * 아래가 **모두** 없을 때만 `true`다 — 상한에 밀려 버린 이벤트, 되살리지
   * 못한 항목, 만들어지지 못하고 거부된 이벤트, 순번 공백·중복·역전·부재,
   * 첫 순번 앞의 설명되지 않는 자리, 손상되거나 읽지 못한 손실 셈, 그리고
   * 저장소 이상. 저장에 실패하면 화면에 보이는 기록이
   * 창을 닫는 순간 사라지므로 완전하다고 말할 수 없다.
   */
  complete(): boolean;
  /** 저장소가 어떤 상태인가. 왜 완전하지 않은지를 밝힌다. */
  storageStatus(): StorageStatus;
  /**
   * 저장 확인 기록 전달 통계 (TASK-2026-003 CR-01 §3.1).
   *
   * 관찰자에게 알리는 도중 생겨 버린 기록 수, 관찰자 예외 수, schema 위반 수다.
   * 완전성 판정(`integrity()`)에는 섞지 않는다 —— 확인 기록은 저장 판정을 관찰할
   * 뿐 바꾸지 않는다.
   */
  confirmationStats(): ConfirmationEmitterStats;
}

/**
 * 저장소 상태 (REV-014).
 *
 * - `ok`: 읽고 쓰고 지울 수 있다.
 * - `unavailable`: 브라우저 저장소를 처음부터 쓸 수 없어 메모리로 물러났다.
 *   이 세션 안에서는 멀쩡히 동작하지만 창을 닫으면 전부 사라진다.
 * - `read_failed`: 읽기가 막혔다. **저장소에 무엇이 들어 있는지 알 수 없으므로
 *   덮어쓰지 않는다.** 이번 세션은 메모리에만 쌓인다(REV-020).
 * - `write_failed`: 쓰기가 막혔거나(용량 초과 등), 던지지 않았는데 적은 값이
 *   되읽히지 않았다(조용한 무시·되읽기 실패, REV-031). 기록은 이 세션의
 *   메모리에만 있고 창을 닫으면 사라진다.
 * - `clear_failed`: 지우기가 막혔거나, 지운 뒤 다시 읽어 비었음을 확인하지
 *   못했다(REV-029). 지웠다고 표시하면 남아 있는 기록을 없는 것으로 오해하게
 *   된다. 로그는 지우기 전 그대로다(REV-028).
 */
export type StorageStatus =
  | 'ok'
  | 'unavailable'
  | 'read_failed'
  | 'write_failed'
  | 'clear_failed';

/**
 * 내보내기 파일의 모양.
 *
 * 배열이 아니라 봉투다. 중앙 명세가 "상한 도달을 성공처럼 숨기지 않는다"를
 * 요구하므로, 파일을 받은 사람이 **이 기록이 완전한지**를 내용과 함께 보아야
 * 한다. 배열만 건네면 잘린 로그와 온전한 로그가 구별되지 않는다.
 */
export interface TelemetryExport {
  readonly schema: string;
  readonly exported_at: number;
  readonly complete: boolean;
  readonly dropped: number;
  /** 되살리지 못한 항목 수. 손상된 기록이 있었다는 뜻이다. */
  readonly rejected: number;
  /** 남은 기록 가운데 비어 있는 순번의 수. */
  readonly sequence_gaps: number;
  /** 순번이 중복되거나 뒤로 돌아간 자리의 수. 순서를 믿을 수 없다는 뜻이다. */
  readonly sequence_conflicts: number;
  /** 순번이 아예 없는 기록의 수. 연속성 검증에 쓸 수 없다. */
  readonly unsequenced: number;
  /** 만들어지지 못하고 거부된 이벤트 수. 계측이 빠진 자리다. */
  readonly invalid: number;
  /** 첫 순번 앞의 설명되지 않는 자리 수. */
  readonly unexplained_leading: number;
  /** 손실 셈을 적어 둔 보조 값이 손상되었는가. 손실 규모를 믿을 수 없다는 뜻이다. */
  readonly meta_corrupt: boolean;
  /** 손실 셈을 읽지 못했는가. 손상과 달리 값 자체를 알 수 없다. */
  readonly meta_unreadable: boolean;
  readonly max_events: number;
  /** 저장소 상태. `ok`가 아니면 이 파일이 가진 것이 전부가 아닐 수 있다. */
  readonly storage: StorageStatus;
  readonly events: readonly TelemetryEvent[];
}

export const EXPORT_SCHEMA = 'ccatlas.telemetry/2';

interface ParseResult {
  readonly events: TelemetryEvent[];
  /**
   * 되살리지 못한 항목 수 (REV-017).
   *
   * 로그 전체가 손상되어 몇 건이었는지 알 수 없을 때는 **최소 1**로 센다.
   * 정확한 수를 모른다는 것이 "아무것도 잃지 않았다"가 되어서는 안 된다.
   */
  readonly rejected: number;
}

/**
 * 저장된 값에서 스키마를 통과하는 이벤트만 되살린다.
 *
 * 버린 건수를 함께 돌려준다. 버리는 것 자체는 옳지만(REV-011), 예전에는
 * 버린 사실을 아무에게도 알리지 않아 구멍 난 로그가 완전한 기록으로
 * 보였다(REV-017).
 */
function parseStored(raw: string | null): ParseResult {
  if (raw === null) {
    return { events: [], rejected: 0 };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // 손상된 로그는 통째로 버린다. 반쪽짜리 기록을 근거로 쓰면 안 된다.
    return { events: [], rejected: 1 };
  }

  if (!Array.isArray(parsed)) {
    return { events: [], rejected: 1 };
  }

  const events: TelemetryEvent[] = [];
  let rejected = 0;

  for (const item of parsed) {
    if (typeof item !== 'object' || item === null) {
      rejected += 1;
      continue;
    }
    const candidate = item as TelemetryEvent;
    if (typeof candidate.payload !== 'object' || candidate.payload === null) {
      rejected += 1;
      continue;
    }
    /*
     * 순번이 망가진 기록은 버린다 (REV-011). `validateEvent`가 모양을 보고,
     * 여기서 통과하지 못한 항목은 되살리지 않는다 — 손상된 순번을 그대로
     * 들이면 그 값을 기준으로 매기는 뒤쪽 순번까지 함께 망가진다.
     */
    if (validateEvent(candidate).length > 0) {
      rejected += 1;
      continue;
    }
    events.push(candidate);
  }

  return { events, rejected };
}

export interface SequenceIssues {
  /**
   * 남은 기록 **가운데** 비어 있는 순번의 수 (REV-017).
   *
   * 손상된 항목은 다음 저장 때 정리되어 사라지지만 `1, 3, 4…`라는 공백은
   * 남는다. 그 파일만 받은 사람도 무언가 빠졌음을 알 수 있어야 한다.
   *
   * 앞쪽 오프셋은 세지 않는다. 상한에 밀려 오래된 기록이 빠지면 남은 순번이
   * 1부터 시작하지 않는데, 그것은 이미 `dropped`가 설명하는 일이므로 두 번
   * 세면 같은 손실을 두 가지 사유로 보고하게 된다.
   */
  readonly gaps: number;
  /**
   * 순번이 앞으로 나아가지 않은 자리의 수 (REV-018).
   *
   * `seq`는 저장 순서를 보증한다. 같은 값이 두 번 나오거나 뒤로 돌아가면
   * 어느 기록이 먼저인지 파일만 보고는 알 수 없다. 공백만 세던 검사는
   * `1, 1, 2`를 온전한 기록으로 통과시켰다.
   *
   * 그런 기록을 **버리지는 않는다.** 실제로 일어난 일의 자료이기 때문이다.
   * 다만 순서를 믿을 수 없다는 사실을 함께 남긴다.
   */
  readonly conflicts: number;
  /**
   * 순번이 아예 없는 기록의 수 (REV-019).
   *
   * 중앙 계약은 모든 공통 이벤트에 순번을 요구한다. 순번이 없으면 서버가
   * 연속성·중복·최종 완료를 검증할 수 없다. 공백도 중복도 아니지만 그 기록을
   * 근거로 쓸 수 없다는 점은 같으므로 별도 사유로 센다.
   *
   * **없던 순번을 지어내지 않는다.** 사후에 번호를 매기면 그 값은 실제 저장
   * 순서를 보증하지 않으면서 보증하는 척을 한다.
   */
  readonly unsequenced: number;
}

/**
 * 순번의 공백과 무결성 위반을 한 번에 센다.
 *
 * 순번이 없는 옛 기록에서는 이어짐을 따질 근거가 없으므로 기준을 끊는다.
 */
function inspectSequence(events: readonly TelemetryEvent[]): SequenceIssues {
  let gaps = 0;
  let conflicts = 0;
  let unsequenced = 0;
  let previous: number | undefined;

  for (const event of events) {
    const seq = event.seq;
    if (typeof seq !== 'number') {
      /*
       * 순번이 없다. 이어짐을 따질 근거가 없으므로 기준을 끊고, 그 사실
       * 자체를 별도로 센다 —— 공백이나 중복으로 세면 같은 기록이 두 가지
       * 사유로 보고된다.
       */
      unsequenced += 1;
      previous = undefined;
      continue;
    }
    if (previous !== undefined) {
      if (seq <= previous) {
        // 중복이거나 뒤로 돌아갔다. 저장 순서를 더는 보증하지 못한다.
        conflicts += 1;
      } else if (seq > previous + 1) {
        gaps += seq - previous - 1;
      }
    }
    previous = seq;
  }

  return { gaps, conflicts, unsequenced };
}

/**
 * 다음 순번. 남아 있는 **정상** 순번의 최댓값 + 1이다.
 *
 * 비어 있거나 순번이 하나도 없으면 1부터 시작한다. 상한에 걸려 앞쪽이 잘려
 * 나가도 남은 기록의 순서는 그대로 이어진다.
 */
function nextSeq(events: readonly TelemetryEvent[]): number {
  let max = 0;
  for (const event of events) {
    const seq = event.seq;
    if (typeof seq === 'number' && Number.isSafeInteger(seq) && seq > max) {
      max = seq;
    }
  }
  return max + 1;
}

export interface TelemetryLogOptions {
  readonly storage?: TelemetryStorage;
  /**
   * 넘긴 저장소가 **창을 닫아도 남는가**. 기본값은 `true`.
   *
   * `resolveBrowserStorage`가 메모리로 물러났다면 `false`를 넘겨야 한다.
   * 그러지 않으면 로그는 잘 저장되고 있다고 믿는다 —— 메모리 저장소는
   * 쓰기가 실패하지 않기 때문이다.
   */
  readonly persistent?: boolean;
  /** 시각 함수. 테스트에서 결정적으로 만들 때 주입한다. */
  readonly now?: () => number;
  /** 같은 page 안의 단조 시계. 있으면 모든 새 이벤트에 segment ID와 elapsed ms를 함께 고정한다. */
  readonly clock?: () => EventClock;
  /**
   * 저장 확인 기록을 받을 관찰자 (TASK-2026-003 CR-01 §3.1). 선택 항목이다.
   *
   * 되읽기로 확인한 결과만 `confirmed`이고 throw·조용한 무시·되읽기 불일치·원문
   * 읽기 실패는 `failed`다. 기록은 **저장소에 쓰지 않으며** 작업이 끝난 뒤 한 번에
   * 넘긴다. 관찰자가 그 자리에서 다시 기록해도 새 확인 기록은 버리고 수만 센다.
   * 관찰자가 없거나 던져도 저장 판정은 같다. 확인 기록을 만들려고 저장소를 더
   * 읽지 않는다(이미 한 되읽기 결과만 쓴다).
   */
  readonly onConfirmation?: ConfirmationSink;
}

/** 저장 키와 확인 기록의 `storage_key` 이름. */
const STORAGE_KEY_NAMES: Readonly<Record<string, StorageKeyName>> = Object.freeze({
  [STORAGE_KEY]: 'events',
  [DROPPED_KEY]: 'dropped',
  [REJECTED_KEY]: 'rejected',
  [INVALID_KEY]: 'invalid',
});

/** 이벤트 목록의 첫·마지막 순번. 순번이 없으면 빈 객체다. */
function seqRange(events: readonly TelemetryEvent[]): { first_seq?: number; last_seq?: number } {
  const seqs = events
    .map((event) => event.seq)
    .filter((seq): seq is number => typeof seq === 'number' && Number.isSafeInteger(seq) && seq >= 1);
  const first = seqs[0];
  const last = seqs[seqs.length - 1];
  return first === undefined || last === undefined ? {} : { first_seq: first, last_seq: last };
}

/**
 * 읽기 자체가 막혔음을 나타내는 표식 (REV-020).
 *
 * `null`(값이 없다)과 구별해야 한다. 둘을 같게 다루면 "저장소가 비어 있다"와
 * "저장소를 읽을 수 없다"가 같은 뜻이 되고, 후자에서 앞선 기록을 덮어쓴다.
 */
const UNREADABLE = Symbol('unreadable');

/**
 * 첫 순번 앞의 설명되지 않는 자리 수 (REV-021).
 *
 * 남은 기록이 6번부터 시작하는데 버린 셈도 되살리지 못한 셈도 0이면, 앞의
 * 다섯은 어디로 갔는지 설명되지 않는다. 상한이나 손상으로 설명되는 경우에는
 * 0으로 둔다 —— 같은 손실을 두 사유로 세지 않기 위해서다.
 *
 * 첫 기록에 순번이 없으면 따질 근거가 없으므로 0이다(그 경우는
 * `unsequenced`가 이미 센다).
 */
function countUnexplainedLeading(
  events: readonly TelemetryEvent[],
  dropped: number,
  rejected: number,
): number {
  if (dropped > 0 || rejected > 0) {
    return 0;
  }
  const first = events[0]?.seq;
  if (typeof first !== 'number' || first <= 1) {
    return 0;
  }
  return first - 1;
}

/** 손실 셈을 적어 두는 보조 값의 이름. */
type CounterName = 'dropped' | 'rejected' | 'invalid';

const COUNTER_KEYS: Readonly<Record<CounterName, string>> = Object.freeze({
  dropped: DROPPED_KEY,
  rejected: REJECTED_KEY,
  invalid: INVALID_KEY,
});

const COUNTER_NAMES: readonly CounterName[] = Object.freeze(['dropped', 'rejected', 'invalid']);

/**
 * 기록 한 번이 저장소에 남기려는 모습 (REV-031).
 *
 * 이벤트와 세 손실 셈을 **쓰기 전에** 함께 계산해 둔다. 쓰기를 확인하지 못하면
 * 이 값이 그대로 세션 사본이 된다 —— 실패한 뒤 저장소를 다시 읽어 사본을 만들지
 * 않는다.
 */
interface PersistTarget extends Readonly<Record<CounterName, number>> {
  readonly events: readonly TelemetryEvent[];
}

/** `clear()`가 지우는 키. 이벤트와 세 손실 셈이다. */
const CLEAR_KEYS: readonly string[] = Object.freeze([STORAGE_KEY, DROPPED_KEY, REJECTED_KEY, INVALID_KEY]);

/** 지우기 전 키 하나의 원문. `symbol`은 읽지 못했다는 표식이다. */
interface ClearOriginal {
  readonly key: string;
  readonly raw: string | null | symbol;
}

/**
 * 보조 값을 읽은 결과 (REV-021).
 *
 * `없음`과 `0`과 `손상`을 구별한다. 예전에는 셋을 모두 0으로 뭉갰고, 그래서
 * 손실 셈이 망가진 로그가 "아무것도 잃지 않았다"로 읽혔다.
 */
interface CounterRead {
  readonly value: number;
  readonly corrupt: boolean;
  /** 저장소 읽기 자체가 막혔다. 손상과는 다른 문제다. */
  readonly unreadable: boolean;
}

export function createTelemetryLog(options: TelemetryLogOptions = {}): TelemetryLog {
  const storage = options.storage ?? createMemoryStorage();
  const now = options.now ?? Date.now;
  const confirmations = createConfirmationEmitter(options.onConfirmation);

  /**
   * 이 저장소로 이룰 수 있는 가장 좋은 상태.
   *
   * 처음부터 메모리로 물러난 환경에서는 아무리 잘 써도 `unavailable`이다.
   * 지우기에 성공해도 여기로 돌아갈 뿐, `ok`가 되지는 않는다.
   */
  const baseline: StorageStatus = options.persistent === false ? 'unavailable' : 'ok';

  let status: StorageStatus = baseline;

  /**
   * 저장소를 더는 믿을 수 없을 때의 세션 사본.
   *
   * 쓰기가 막히거나(용량 초과) 읽기가 막히면 여기로 물러난다. 창을 닫으면
   * 사라지지만, 적어도 이번 세션의 기록은 잃지 않고 그 사실이 상태에 남는다.
   */
  let memory: TelemetryEvent[] | undefined;
  const memoryCounters: Record<CounterName, number> = { dropped: 0, rejected: 0, invalid: 0 };
  let memoryMetaCorrupt = false;
  /**
   * 손실 셈을 **읽지 못한** 적이 있는가 (REV-025).
   *
   * 손상(값이 망가짐)과 다른 사유다. 값이 무엇인지 모르는 것이므로 0으로
   * 갈음할 수 없고, 그 값을 덮어써서도 안 된다. 한 번 모르게 되면 이 세션
   * 내내 모르는 상태이므로 되돌리지 않는다 —— `clear()`만 되돌린다.
   */
  let metaUnreadable = false;

  /** 저장소에서 값 하나를 읽는다. 읽기 자체가 막히면 그 사실을 돌려준다. */
  function rawItem(key: string): string | null | typeof UNREADABLE {
    try {
      return storage.getItem(key);
    } catch {
      return UNREADABLE;
    }
  }

  function readCounter(name: CounterName): CounterRead {
    if (memory !== undefined) {
      return { value: memoryCounters[name], corrupt: memoryMetaCorrupt, unreadable: false };
    }

    const raw = rawItem(COUNTER_KEYS[name]);
    if (raw === UNREADABLE) {
      /*
       * 예전에는 이 사실을 여기서만 만들고 판정까지 가져가지 않았다. 그래서
       * 손실 셈 하나만 읽히지 않는 저장소가 "아무것도 잃지 않은 완전한
       * 로그"로 보고됐다(REV-025).
       */
      metaUnreadable = true;
      return { value: 0, corrupt: false, unreadable: true };
    }
    if (raw === null) {
      // 아직 아무것도 잃지 않았다. 없는 것과 손상된 것은 다르다.
      return { value: 0, corrupt: false, unreadable: false };
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { value: 0, corrupt: true, unreadable: false };
    }
    if (typeof parsed !== 'number' || !Number.isSafeInteger(parsed) || parsed < 0) {
      return { value: 0, corrupt: true, unreadable: false };
    }
    return { value: parsed, corrupt: false, unreadable: false };
  }

  /**
   * 저장소를 포기하고 메모리로 물러난다.
   *
   * 물러나기 **전에** 지금까지의 손실 셈을 건져 둔다 (REV-022). 그러지 않으면
   * 이미 다섯 건을 잃은 로그가 쓰기 실패를 만나는 순간 "아무것도 잃지 않은
   * 로그"로 바뀐다 —— 상태만 나빠지고 손실 규모는 사라진다.
   */
  function fallBackToMemory(reason: StorageStatus, events: readonly TelemetryEvent[]): void {
    if (memory === undefined) {
      for (const name of COUNTER_NAMES) {
        const read = readCounter(name);
        memoryCounters[name] = read.value;
        if (read.corrupt) {
          memoryMetaCorrupt = true;
        }
      }
    }
    memory = [...events];
    status = reason;
  }

  /**
   * 저장소를 한 번 읽는다. 되살린 이벤트와 버린 건수를 함께 돌려준다.
   *
   * **읽는 것만으로는 셈을 옮기지 않는다.** 손상된 항목은 아직 저장소에
   * 그대로 있으므로 다음에 읽어도 다시 세어진다. 읽을 때마다 누적하면
   * 화면을 새로 그릴 때마다 잃은 건수가 불어난다.
   */
  function readState(): ParseResult {
    if (memory !== undefined) {
      return { events: [...memory], rejected: 0 };
    }

    const raw = rawItem(STORAGE_KEY);
    if (raw === UNREADABLE) {
      /*
       * 읽을 수 없다 (REV-020). 저장소에 무엇이 들어 있는지 모르므로 **덮어쓰지
       * 않는다.** 예전에는 빈 배열로 시작해 그 위에 새 기록을 썼고, 그래서
       * 읽기만 막힌 저장소에서 앞선 기록이 매번 지워졌다.
       */
      fallBackToMemory('read_failed', []);
      return { events: [], rejected: 0 };
    }

    const parsed = parseStored(raw);

    /*
     * 손실 셈의 읽기도 **여기서** 확인한다 (REV-026).
     *
     * 예전에는 보조 값을 `assess()`만 읽었다. 그래서 `storageStatus()`처럼 이
     * 함수만 거치는 질의를 먼저 하면, 보조 값이 읽히지 않는 저장소에서도
     * `ok`를 답했다. 모든 질의와 기록이 이 함수를 거치므로 여기서 확인하면
     * 어느 창구로 먼저 묻든 같은 상태가 나온다.
     */
    if (COUNTER_NAMES.some((name) => rawItem(COUNTER_KEYS[name]) === UNREADABLE)) {
      metaUnreadable = true;
      fallBackToMemory('read_failed', parsed.events);
      /*
       * 메모리로 옮기면 손상 항목은 사본에 들어가지 않고 다음 읽기부터 세어지지
       * 않는다. 물러나기 전에 그 셈을 사본의 셈으로 옮긴다(REV-022와 같은 이유).
       * 저장소의 원본은 건드리지 않는다.
       */
      memoryCounters.rejected += parsed.rejected;
      return { events: parsed.events, rejected: 0 };
    }

    return parsed;
  }

  function load(): TelemetryEvent[] {
    return readState().events;
  }

  /**
   * 적고 **되읽어** 확인한다 (REV-031).
   *
   * `setItem`이 던지지 않았다고 저장된 것은 아니다. 쓰기를 조용히 무시하는
   * 저장소에서 기록이 하나도 남지 않았는데 `storage: ok`, `complete: true`인 빈
   * 로그가 내보내졌다. 던짐·조용한 무시·되읽기 실패를 가리지 않고 모두 실패다.
   */
  function writeChecked(key: string, value: string): FailureReason | null {
    try {
      storage.setItem(key, value);
    } catch {
      return 'threw';
    }
    return readbackReason(key, value);
  }

  /** 이미 쓴 키를 한 번 되읽어 사유를 정한다. `null`이면 기대한 값이 읽혔다. */
  function readbackReason(key: string, expected: string | null): FailureReason | null {
    const echo = rawItem(key);
    if (echo === UNREADABLE) {
      return 'readback_failed';
    }
    return echo === expected ? null : 'readback_mismatch';
  }

  /**
   * 저장 확인 기록 하나를 모은다 (CR-01 §3.1).
   *
   * 저장소를 읽지 않는다. 사유는 호출자가 이미 한 쓰기·되읽기의 결과다. 처음부터
   * 메모리로 물러난 환경(`unavailable`)의 되읽기는 창을 닫으면 사라지는 값이므로
   * `confirmed`로 보고하지 않는다. hash는 관찰자가 있을 때만 계산한다.
   */
  function note(
    operation: 'telemetry_events' | 'telemetry_counter' | 'telemetry_restore' | 'telemetry_clear',
    key: string,
    reason: FailureReason | null,
    content: string | null,
    events?: readonly TelemetryEvent[],
  ): void {
    const storageKey = STORAGE_KEY_NAMES[key];
    if (storageKey === undefined) {
      return;
    }
    confirmations.collect(() => {
      const unavailable = reason === null && baseline !== 'ok';
      return {
        verifiedBy: 'browser_storage_readback',
        operation,
        key: { storage_key: storageKey, ...(events === undefined ? {} : seqRange(events)) },
        result: reason === null && !unavailable ? 'confirmed' : 'failed',
        reason: unavailable ? 'storage_unavailable' : reason,
        // 주입된 `now`를 쓰지 않는다. 관찰자 유무가 이벤트 시각(`at`)을 바꾸면 안 된다.
        checkedAt: Date.now(),
        contentHash: content === null ? null : textHash(content),
      };
    });
  }

  /**
   * 원문으로 되돌리고 되읽어 확인한다 (REV-031).
   *
   * 원문이 없었으면 지우고 비었는지 본다. 원문을 읽지 못한 키는 애초에 쓰지
   * 않으므로 여기 오지 않는다.
   */
  function restoreChecked(key: string, raw: string | null): FailureReason | null {
    if (raw === null) {
      try {
        storage.removeItem(key);
      } catch {
        return 'threw';
      }
      return readbackReason(key, null);
    }
    return writeChecked(key, raw);
  }

  /**
   * 목표 상태를 저장소에 반영한다 (REV-014, REV-031).
   *
   * 바뀐 손실 셈을 먼저, 이벤트를 나중에 쓰고 **하나씩 되읽어** 확인한다. 하나라도
   * 확인되지 않으면 거기서 멈추고 목표 상태를 그대로 세션 사본으로 옮긴 뒤
   * `write_failed`로 둔다.
   *
   * 물러날 때 저장소를 **다시 읽지 않는다.** 예전에는 실패한 뒤 저장소를 다시
   * 읽어 사본을 만들었는데, 그러면 이미 셈으로 옮긴 손상 항목을 한 번 더 세거나,
   * 방금 되읽기에 실패한 키를 또 읽다가 다른 사유로 물러날 수 있다. 목표는 쓰기
   * 전에 계산돼 있으므로 그것이 곧 사본이다. 확인하지 못한 키를 다시 쓰지도
   * 않는다 —— 무엇이 적혀 있는지 모르는 저장소를 덮어쓰지 않는다(REV-020·025).
   *
   * **앞서 성공한 쓰기는 되돌린다.** 거부 셈은 적혔는데 이벤트 쓰기만 실패하면
   * 저장소에 늘어난 셈과 아직 정리되지 않은 손상 원문이 함께 남고, 재시작하면
   * 같은 손상 항목을 두 번 센다. 쓰기 전에 키마다 원문을 확보해 두었다가, 실패하면
   * 시도한 키를 거꾸로 원문으로 되돌리고 되읽어 확인한다. 되돌리기가 실패해도
   * 이번 세션은 사본과 `write_failed`로 지킨다(그때 남는 과대 보고는 잔여 위험).
   *
   * **원문을 읽지 못한 키에는 쓰지 않는다.** 되돌릴 값을 모르기 때문이다. 쓰면
   * 뒤에서 실패해도 모르는 값을 덮어쓴 채 남는다. 그 키 앞에서 `read_failed`로
   * 멈추고 이미 성공한 앞선 키만 되돌린다(REV-020·025).
   *
   * 손실 셈은 늘기만 하므로 목표를 쓰는 삭제는 없다. 삭제는 원문이 없던 키를
   * 되돌릴 때뿐이다. 0으로 되돌리는 사용자 삭제는 `clear()`이며, 그쪽은 되읽어
   * 확인하고 `clear_failed`로 알린다(REV-029).
   */
  function persist(
    target: PersistTarget,
    counterWrites: readonly CounterName[],
    writeEvents: boolean,
    metaCorrupt: boolean,
  ): void {
    const alreadyMemory = memory !== undefined;
    if (memory === undefined) {
      const writes: (readonly [string, string])[] = counterWrites.map(
        (name) => [COUNTER_KEYS[name], JSON.stringify(target[name])] as const,
      );
      if (writeEvents) {
        writes.push([STORAGE_KEY, JSON.stringify(target.events)]);
      }

      const attempted: { readonly key: string; readonly raw: string | null }[] = [];
      let failure: StorageStatus | undefined;
      for (const [key, value] of writes) {
        const raw = rawItem(key);
        const operation = key === STORAGE_KEY ? 'telemetry_events' : 'telemetry_counter';
        const range = key === STORAGE_KEY ? target.events : undefined;
        if (raw === UNREADABLE) {
          // 원문을 모른다. 이 키는 시도하지 않는다. 원인이 읽기이므로 `read_failed`다.
          note(operation, key, 'original_unreadable', value, range);
          failure = 'read_failed';
          break;
        }
        // 실패한 쓰기도 일부 반영됐을 수 있으므로(되읽기 실패) 되돌릴 대상에 넣는다.
        attempted.push({ key, raw });
        const reason = writeChecked(key, value);
        note(operation, key, reason, value, range);
        if (reason !== null) {
          failure = 'write_failed';
          break;
        }
      }
      if (failure === undefined) {
        return;
      }

      for (const { key, raw } of [...attempted].reverse()) {
        // 결과와 무관하게 이번 세션은 아래의 사본이 원본이다.
        note('telemetry_restore', key, restoreChecked(key, raw), raw);
      }
      memoryMetaCorrupt = metaCorrupt;
      status = failure;
    }
    if (alreadyMemory && writeEvents) {
      // 저장소에 쓰지 않았다. 사본에만 있다는 사실을 확인 기록에서도 숨기지 않는다.
      note('telemetry_events', STORAGE_KEY, 'storage_unavailable', null, target.events);
    }
    // 이미 저장소를 포기했거나 방금 포기했다. 이번 세션은 사본이 원본이다.
    memory = [...target.events];
    for (const name of COUNTER_NAMES) {
      memoryCounters[name] = target[name];
    }
  }

  /** 지금까지의 손실 셈과 손상 여부를 한 번에 모은다. */
  function counters(): {
    readonly dropped: number;
    readonly rejected: number;
    readonly invalid: number;
    readonly corrupt: boolean;
    readonly unreadable: boolean;
  } {
    const dropped = readCounter('dropped');
    const rejected = readCounter('rejected');
    const invalid = readCounter('invalid');
    return {
      dropped: dropped.value,
      rejected: rejected.value,
      invalid: invalid.value,
      corrupt: dropped.corrupt || rejected.corrupt || invalid.corrupt,
      // 셋 중 하나라도 읽지 못했으면 손실 규모 전체를 믿을 수 없다.
      unreadable: metaUnreadable || dropped.unreadable || rejected.unreadable || invalid.unreadable,
    };
  }

  /**
   * 손실 셈을 읽지 못했다면 저장소 쓰기를 멈춘다 (REV-025).
   *
   * 값이 무엇인지 모르는 채로 쓰면 실제 셈을 0에서 다시 세게 되어 지워진다.
   * 읽을 수 없는 저장소를 덮어쓰지 않는다는 REV-020의 규칙을 보조 값에도
   * 그대로 적용한다.
   */
  function guardCounterReads(events: readonly TelemetryEvent[]): void {
    if (metaUnreadable && memory === undefined) {
      fallBackToMemory('read_failed', events);
    }
  }

  /**
   * 한눈에 보는 완전성 (REV-020~024).
   *
   * 사유를 하나씩 따로 묻지 않고 여기서 모은다. 화면·내보내기·`complete()`가
   * 같은 값을 보아야 셋이 서로 다른 말을 하지 않는다.
   */
  function assess(): {
    readonly events: readonly TelemetryEvent[];
    readonly dropped: number;
    readonly rejected: number;
    readonly invalid: number;
    readonly sequence: SequenceIssues;
    readonly unexplainedLeading: number;
    readonly metaCorrupt: boolean;
    readonly metaUnreadable: boolean;
    readonly complete: boolean;
  } {
    const state = readState();
    // `readState`가 메모리로 물러났을 수 있으므로 셈은 그 뒤에 읽는다.
    const counts = counters();
    // 보조 값을 읽지 못했다면 여기서 물러난다. 이후 쓰기가 실제 셈을 지운다.
    guardCounterReads(state.events);
    const rejected = counts.rejected + state.rejected;
    const sequence = inspectSequence(state.events);
    const unexplainedLeading = countUnexplainedLeading(state.events, counts.dropped, rejected);

    return {
      events: state.events,
      dropped: counts.dropped,
      rejected,
      invalid: counts.invalid,
      sequence,
      unexplainedLeading,
      metaCorrupt: counts.corrupt,
      metaUnreadable: counts.unreadable,
      complete:
        counts.dropped === 0 &&
        rejected === 0 &&
        counts.invalid === 0 &&
        sequence.gaps === 0 &&
        sequence.conflicts === 0 &&
        sequence.unsequenced === 0 &&
        unexplainedLeading === 0 &&
        !counts.corrupt &&
        !counts.unreadable &&
        status === 'ok',
    };
  }

  /**
   * 지우기에 실패했다. 지우기 전의 로그로 되돌린다 (REV-028, REV-029).
   *
   * `false`를 돌려줄 때 로그는 지우기 전과 같아야 한다. 화면은 실패를 보고
   * 계보를 끊지 않으므로, 기록이 일부라도 사라져 있으면 다음 시도가 로그에
   * 없는 시도를 가리킨다.
   */
  function abandonClear(
    before: ParseResult,
    counts: ReturnType<typeof counters>,
    attempted: readonly ClearOriginal[],
  ): void {
    let restored = true;
    for (const { key, raw } of attempted) {
      if (raw === null) {
        continue;
      }
      if (typeof raw !== 'string') {
        // 읽지 못했다. 무엇이었는지 모르는 값은 되돌릴 수 없다.
        note('telemetry_restore', key, 'original_unreadable', null);
        restored = false;
        continue;
      }
      try {
        storage.setItem(key, raw);
      } catch {
        note('telemetry_restore', key, 'threw', raw);
        restored = false;
        continue;
      }
      // 되쓰기도 조용히 무시될 수 있다. 삭제와 같이 읽어서 확인한다(REV-029).
      const reason = readbackReason(key, raw);
      note('telemetry_restore', key, reason, raw);
      if (reason !== null) {
        restored = false;
      }
    }
    if (!restored && memory === undefined) {
      /*
       * 저장소를 되돌리지 못했다. 저장소의 로그는 더는 이번 세션을 담고
       * 있지 않으므로, 지우기 전의 기록과 손실 셈을 세션 사본으로 옮긴다.
       * 저장소에서 다시 읽으면 이미 일부가 지워진 값이 나온다.
       */
      memory = [...before.events];
      memoryCounters.dropped = counts.dropped;
      memoryCounters.rejected = counts.rejected + before.rejected;
      memoryCounters.invalid = counts.invalid;
      memoryMetaCorrupt = counts.corrupt;
    }
    status = 'clear_failed';
  }

  return {
    record(name, payload = {}) {
      let event: TelemetryEvent;
      try {
        event = createEvent(name, payload, now(), options.clock?.());
      } catch {
        /*
         * 스키마 위반은 개발 중 버그다. 로그를 오염시키느니 버리되, **버린
         * 사실은 센다** (REV-023). 호출자는 반환값을 보지 않으므로, 여기서
         * 세지 않으면 계측이 통째로 빠진 로그가 완전한 기록으로 보인다.
         *
         * 이벤트는 쓰지 않으므로 손상 항목은 저장소에 그대로 남는다. 다만 셈을
         * 확인하지 못해 사본으로 물러나면 손상 항목은 사본에 들어가지 않으므로,
         * 사본의 거부 셈에는 미리 더해 둔다(REV-022와 같은 이유).
         */
        const state = readState();
        const counts = counters();
        persist(
          {
            events: state.events,
            dropped: counts.dropped,
            rejected: counts.rejected + state.rejected,
            invalid: counts.invalid + 1,
          },
          ['invalid'],
          false,
          counts.corrupt,
        );
        confirmations.flush();
        return undefined;
      }

      const state = readState();
      // `readState`가 메모리로 물러났을 수 있으므로 셈은 그 뒤에 읽는다.
      const counts = counters();

      /*
       * 순번은 **저장하는 쪽**이 매긴다 (SPEC 「포함 범위」 4).
       *
       * `at`만으로는 순서를 다툴 수 없다. 같은 밀리초에 여러 건이 들어오고
       * (한 수가 시도를 끝내면 종료·경로·튜토리얼 완료가 한꺼번에 남는다),
       * 기기 시각이 바뀌면 뒤로 돌기도 한다.
       *
       * **마지막 값이 아니라 최댓값에 1을 더한다.** 마지막 값만 보면, 손상된
       * 기록 하나가 그 뒤의 순번을 전부 망가뜨린다(REV-011).
       */
      const stamped: TelemetryEvent = { ...event, seq: nextSeq(state.events) };
      const events = [...state.events, stamped];

      /*
       * 상한을 넘으면 오래된 것부터 버리되 **버렸다는 사실을 남긴다**
       * (중앙 명세 「시도 정확성」: 상한 도달을 성공처럼 숨기지 않는다).
       */
      const overflow = Math.max(0, events.length - MAX_EVENTS);

      /*
       * 손상된 항목은 이 저장으로 정리되어 사라진다. 사라지기 전에 셈을
       * 옮겨 둔다 —— 그러지 않으면 "무언가 잃었다"는 사실만 조용히
       * 없어진다(REV-017). 셈을 먼저 쓰고 이벤트를 나중에 쓴다.
       */
      const counterWrites: CounterName[] = [];
      if (state.rejected > 0) {
        counterWrites.push('rejected');
      }
      if (overflow > 0) {
        counterWrites.push('dropped');
      }

      persist(
        {
          events: events.slice(overflow),
          dropped: counts.dropped + overflow,
          rejected: counts.rejected + state.rejected,
          invalid: counts.invalid,
        },
        counterWrites,
        true,
        counts.corrupt,
      );
      confirmations.flush();
      return stamped;
    },

    readAll() {
      return Object.freeze(load());
    },

    clear() {
      /*
       * 지웠다고 말하려면 실제로 지워져야 한다. 실패를 삼키면 화면은 0건을
       * 보여 주는데 저장소에는 그대로 남아 있게 된다(REV-014).
       *
       * 거꾸로 **지우지 못했다고 말하면 아무것도 지워져 있지 않아야 한다**
       * (REV-028). 네 키를 차례로 지우므로 뒤쪽 키에서 막히면 앞쪽 키는 이미
       * 사라진 뒤다. 화면은 실패를 보고 계보를 끊지 않는데 기록은 없어서, 다음
       * 시도가 로그에 없는 시도를 가리켰다. 그래서 지우기 전의 모습을 붙들어
       * 두고, 막히면 지운 키를 되돌린다.
       */
      const before = readState();
      const counts = counters();
      const originals: ClearOriginal[] = CLEAR_KEYS.map((key) => ({ key, raw: rawItem(key) }));
      const attempted: ClearOriginal[] = [];
      let threw = false;
      try {
        for (const original of originals) {
          // 던진 삭제도 지워졌는지 알 수 없으므로 되돌릴 대상에 넣는다.
          attempted.push(original);
          storage.removeItem(original.key);
        }
      } catch {
        threw = true;
      }
      if (confirmations.enabled) {
        /*
         * 확인 기록용으로만 키마다 사유를 정한다. 판정은 아래의 기존 검사가 한다.
         * 관찰자가 있을 때는 시도한 키를 모두 되읽으므로 읽기 횟수가 늘 수 있으나
         * 읽기는 상태를 바꾸지 않는다.
         */
        attempted.forEach(({ key }, index) => {
          const reason = threw && index === attempted.length - 1 ? 'threw' : readbackReason(key, null);
          note('telemetry_clear', key, reason, null);
        });
      }

      /*
       * **던지지 않았다고 지워진 것은 아니다** (REV-029).
       *
       * 삭제를 조용히 무시하는 저장소가 있다. 예전에는 예외가 없으면 `true`를
       * 돌려주었고, 화면은 계보를 끊었는데 기록은 저장소에 남아 다음 접속 때
       * 되살아났다. 네 키를 다시 읽어 모두 비었을 때만 지웠다고 말한다. 읽기가
       * 막히면 지워졌는지 모르므로 역시 실패다.
       */
      if (threw || !CLEAR_KEYS.every((key) => rawItem(key) === null)) {
        abandonClear(before, counts, attempted);
        confirmations.flush();
        return false;
      }

      memory = undefined;
      memoryCounters.dropped = 0;
      memoryCounters.rejected = 0;
      memoryCounters.invalid = 0;
      memoryMetaCorrupt = false;
      metaUnreadable = false;
      status = baseline;

      /*
       * 지웠다고 저장소가 고쳐지지는 않는다. 읽기가 여전히 막혀 있으면 그
       * 상태로 되돌아가야 한다 —— 지우기 한 번으로 `ok`가 되면 안 된다.
       * 이벤트 키와 손실 셈의 읽기를 같은 경로로 다시 확인한다(REV-026).
       */
      readState();
      confirmations.flush();
      return true;
    },

    toJson() {
      const view = assess();
      const payload: TelemetryExport = {
        schema: EXPORT_SCHEMA,
        exported_at: now(),
        complete: view.complete,
        dropped: view.dropped,
        rejected: view.rejected,
        invalid: view.invalid,
        sequence_gaps: view.sequence.gaps,
        sequence_conflicts: view.sequence.conflicts,
        unsequenced: view.sequence.unsequenced,
        unexplained_leading: view.unexplainedLeading,
        meta_corrupt: view.metaCorrupt,
        meta_unreadable: view.metaUnreadable,
        max_events: MAX_EVENTS,
        storage: status,
        events: view.events,
      };
      return JSON.stringify(payload, null, 2);
    },

    size() {
      return load().length;
    },

    dropped() {
      return assess().dropped;
    },

    rejected() {
      return assess().rejected;
    },

    invalid() {
      return assess().invalid;
    },

    sequenceGaps() {
      return assess().sequence.gaps;
    },

    sequenceConflicts() {
      return assess().sequence.conflicts;
    },

    unsequenced() {
      return assess().sequence.unsequenced;
    },

    unexplainedLeading() {
      return assess().unexplainedLeading;
    },

    metaCorrupt() {
      return assess().metaCorrupt;
    },

    metaUnreadable() {
      return assess().metaUnreadable;
    },

    complete() {
      return assess().complete;
    },

    storageStatus() {
      // 상태는 읽기 시도 중에 바뀔 수 있다. 먼저 한 번 읽고 나서 답한다.
      readState();
      return status;
    },

    confirmationStats() {
      return confirmations.stats();
    },

    integrity() {
      const view = assess();
      return {
        complete: view.complete,
        storage: status,
        dropped: view.dropped,
        rejected: view.rejected,
        invalid: view.invalid,
        sequenceGaps: view.sequence.gaps,
        sequenceConflicts: view.sequence.conflicts,
        unsequenced: view.sequence.unsequenced,
        unexplainedLeading: view.unexplainedLeading,
        metaCorrupt: view.metaCorrupt,
        metaUnreadable: view.metaUnreadable,
      };
    },
  };
}

/**
 * 내보내기 파일 이름. 전역 UI/UX 표준의
 * `[Service]_[Category]_[Data]_[Period].[Ext]` 규칙을 따른다.
 */
export function exportFileName(at: number = Date.now()): string {
  const date = new Date(at);
  const pad = (value: number): string => String(value).padStart(2, '0');
  const stamp =
    `${String(date.getFullYear())}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
    `-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  return `ColorCubeAtlas_Telemetry_Local_${stamp}.json`;
}
